import React, { useMemo, useCallback, useState } from 'react';
import {
  View,
  Text,
  Platform,
  ScrollView,
  useWindowDimensions,
  Alert,
  RefreshControl,
} from 'react-native';
import Animated from 'react-native-reanimated';
import { Clickable2 } from '../../components/common/Clickable2';
import { SvgIcon } from '../../components/common/SvgIcon';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { AppLinearGradient as LinearGradient } from '../../components/ui/AppLinearGradient';
import { type ThemeColors, appTheme } from '../../shared/theme';
import { generateShade, hexToRgba } from '../../shared/theme/colorUtils';
import { useTranslation, getApiText, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { formatCurrency, getDefaultCurrencyCode } from '../../shared/utils/currencyUtils';
import { firstPlanImageFromCustomCategory, sortByDisplayOrder } from '../../data/domains/subscription';
import { resolveAttachmentUri } from '../../shared/utils/resolveMediaUri';
import { type SubscriptionListStyles } from './subscriptionListStyles';
import { CategorySlider } from './SubscriptionListCategorySlider';
import type { CustomCheckoutPayload } from './subscriptionListTypes';
import AppIcon from '../../components/common/icons/AppIcon';
import { androidScaleSize } from '../../shared/theme/androidUiParity';

const ANDROID_TEXT = Platform.OS === 'android' ? { includeFontPadding: false } : {};

type EditorKind = 'duration' | 'meals' | 'snacks' | `meal-${number}` | null;
type MealMacros = { protein: string; carbs: string };

function SummaryRow({
  label,
  value,
  onPress,
  styles,
  themeColors,
  chevron,
  is_last,
}: {
  label: string;
  value: string;
  onPress: () => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  chevron: 'arrow-left' | 'arrow-right';
  is_last?: boolean;
}) {
  const { textAlign, writingDirection, flipRowStyle } = useRtl();
  return (
    <Clickable2
      style={[styles.customSummaryRow, !is_last && styles.customSummaryRowBorder, flipRowStyle]}
      onPress={onPress}
      haptics
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}>
      <Text style={[styles.customSummaryLabel, { textAlign, writingDirection, color: themeColors.textSecondary }, ANDROID_TEXT]}>
        {label}
      </Text>
      <View style={[styles.customSummaryValueWrap, flipRowStyle]}>
        <Text style={[styles.customSummaryValue, { color: themeColors.text }, ANDROID_TEXT]} numberOfLines={1}>
          {value}
        </Text>
        <SvgIcon name={chevron} size={16} color={themeColors.textLight} />
      </View>
    </Clickable2>
  );
}

/** One setup row: Meals (n) + Snacks (n) as tappable count pills. */
function MealsSnacksSetupRow({
  meals_label,
  snacks_label,
  meal_count,
  snack_count,
  onPressMeals,
  onPressSnacks,
  styles,
  themeColors,
  is_last,
}: {
  meals_label: string;
  snacks_label: string;
  meal_count: number;
  snack_count: number;
  onPressMeals: () => void;
  onPressSnacks: () => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  is_last?: boolean;
}) {
  const { flipRowStyle } = useRtl();
  return (
    <View style={[styles.customMealsSnacksRow, !is_last && styles.customSummaryRowBorder, flipRowStyle]}>
      <Clickable2
        style={[styles.customCountPill, flipRowStyle]}
        onPress={onPressMeals}
        haptics
        accessibilityRole="button"
        accessibilityLabel={`${meals_label}: ${formatNumber(meal_count)}`}>
        <Text style={[styles.customCountPillLabel, { color: themeColors.textSecondary }, ANDROID_TEXT]} numberOfLines={1}>
          {meals_label}
        </Text>
        <Text style={[styles.customCountPillValue, { color: themeColors.text }, ANDROID_TEXT]} numberOfLines={1}>
          {formatNumber(meal_count)}
        </Text>
      </Clickable2>
      <View style={[styles.customCountPillDivider, { backgroundColor: hexToRgba(themeColors.border, 0.55) as string }]} />
      <Clickable2
        style={[styles.customCountPill, flipRowStyle]}
        onPress={onPressSnacks}
        haptics
        accessibilityRole="button"
        accessibilityLabel={`${snacks_label}: ${formatNumber(snack_count)}`}>
        <Text style={[styles.customCountPillLabel, { color: themeColors.textSecondary }, ANDROID_TEXT]} numberOfLines={1}>
          {snacks_label}
        </Text>
        <Text style={[styles.customCountPillValue, { color: themeColors.text }, ANDROID_TEXT]} numberOfLines={1}>
          {formatNumber(snack_count)}
        </Text>
      </Clickable2>
    </View>
  );
}

function MacroChip({
  kind,
  grams,
  styles,
  themeColors,
  a11y_label,
}: {
  kind: 'protein' | 'carbs';
  grams?: string;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  a11y_label: string;
}) {
  const tone2 = kind === 'protein' ? themeColors.protein : themeColors.carbs;
  const tone = tone2;
  const short = kind === 'protein' ? 'P' : 'C';
  const has_value = Boolean(grams);
  const grams_label = has_value ? formatNumber(Number(grams ?? 0) || 0) : '—';
  const wash = kind === 'protein' ? themeColors.proteinUltra : themeColors.carbsUltra;
  const border = kind === 'protein' ? themeColors.protein : themeColors.carbs;

  return (
    <View
      style={[styles.customMacroChip, { backgroundColor: wash, borderColor: border }]}
      accessibilityLabel={`${a11y_label} ${has_value ? `${grams_label}g` : ''}`}>
      <Text style={[styles.customMacroChipKey, { color: tone }, ANDROID_TEXT]}>{short}</Text>
      <Text
        style={[
          styles.customMacroChipText,
          { color: has_value ? themeColors.text : themeColors.textLight },
          ANDROID_TEXT,
        ]}
        numberOfLines={1}>
        {grams_label}
        {has_value ? (
          <Text style={[styles.customMacroChipGrams, { color: themeColors.textSecondary }, ANDROID_TEXT]}> g</Text>
        ) : null}
      </Text>
    </View>
  );
}

function MealSlot({
  index,
  protein,
  carbs,
  onPress,
  styles,
  themeColors,
  chevron,
  meal_label,
  set_macros_hint,
  scaled_protein_label,
  scaled_carbs_label,
}: {
  index: number;
  protein?: string;
  carbs?: string;
  onPress: () => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  chevron: 'arrow-left' | 'arrow-right';
  meal_label: string;
  set_macros_hint: string;
  scaled_protein_label: string;
  scaled_carbs_label: string;
}) {
  const { textAlign, writingDirection, flipRowStyle } = useRtl();
  const filled = Boolean(protein && carbs);
  const partial = Boolean(protein || carbs) && !filled;
  const a11y_value = filled
    ? `${scaled_protein_label} ${protein}g, ${scaled_carbs_label} ${carbs}g`
    : partial
      ? [
          protein ? `${scaled_protein_label} ${protein}g` : null,
          carbs ? `${scaled_carbs_label} ${carbs}g` : null,
        ]
          .filter(Boolean)
          .join(', ')
      : set_macros_hint;

  return (
    <Clickable2
      style={[styles.customMealSlot, (filled || partial) && styles.customMealSlotFilled, flipRowStyle]}
      onPress={onPress}
      haptics
      accessibilityRole="button"
      accessibilityLabel={`${meal_label}: ${a11y_value}`}>
     {filled || partial ? (
      <View style={[styles.customMealIndex, filled && styles.customMealIndexFilled]}>
          <AppIcon name="check" size={androidScaleSize(16)} color={themeColors.success} style={styles.customMealIndexIcon}/>
   
            <Text style={[styles.customMealIndexTextFilled, { textAlign, writingDirection }, ANDROID_TEXT]} numberOfLines={1}>
            {meal_label}
          </Text>
          </View>
    ) : 
    <>
    <View style={[styles.customMealIndex]}>
    <Text
      style={[styles.customMealIndexText, ANDROID_TEXT]}
      numberOfLines={1}>
      {formatNumber(index + 1)}
    </Text>
  </View>
        <Text style={[styles.customMealSlotLabel, { textAlign, writingDirection }, ANDROID_TEXT]} numberOfLines={1}>
        {meal_label}
      </Text>
      </>
    }
      

      
      <View style={[styles.customMealSlotTrailing, flipRowStyle]}>
        {filled || partial ? (
          <View style={[styles.customMacroChipsRow, flipRowStyle]}>
            <MacroChip
              kind="protein"
              grams={protein}
              styles={styles}
              themeColors={themeColors}
              a11y_label={scaled_protein_label}
            />
            <MacroChip
              kind="carbs"
              grams={carbs}
              styles={styles}
              themeColors={themeColors}
              a11y_label={scaled_carbs_label}
            />
          </View>
        ) : (
          <Text style={[styles.customMealSelectHint, { textAlign, writingDirection }, ANDROID_TEXT]} numberOfLines={1}>
            {set_macros_hint}
          </Text>
        )}
        <View style={styles.customMealChevron}>
          <AppIcon name={chevron} size={16} color={themeColors.text} />
        </View>
      </View>
    </Clickable2>
  );
}

function OptionRow({
  label,
  selected,
  onPress,
  styles,
  themeColors,
  compact,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  compact?: boolean;
}) {
  const { textAlign, writingDirection, flipRowStyle } = useRtl();
  return (
    <Clickable2
      style={[
        compact ? styles.customOptionRowCompact : styles.customOptionRow,
        !compact && flipRowStyle,
        selected
          ? { backgroundColor: themeColors.primary, borderColor: themeColors.primary }
          : { backgroundColor: themeColors.surfaceElevated, borderColor: themeColors.border },
      ]}
      onPress={onPress}
      haptics
      accessibilityRole="button"
      accessibilityState={{ selected }}>
      <Text
        style={[
          compact ? styles.customOptionRowTextCompact : styles.customOptionRowText,
          {
            textAlign: compact ? 'center' : textAlign,
            writingDirection,
            color: selected ? themeColors.primaryText : themeColors.text,
          },
          ANDROID_TEXT,
        ]}
        numberOfLines={1}>
        {label}
      </Text>
      {!compact && selected ? <SvgIcon name="check" size={16} color={themeColors.primaryText} /> : null}
    </Clickable2>
  );
}

function CountGrid({
  values,
  selected,
  onSelect,
  styles,
  themeColors,
  accessibility_prefix,
}: {
  values: number[];
  selected: number;
  onSelect: (n: number) => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  accessibility_prefix: string;
}) {
  return (
    <View style={styles.customCountGrid}>
      {values.map(n => {
        const is_selected = selected === n;
        return (
          <Clickable2
            key={n}
            style={[
              styles.customCountTile,
              is_selected
                ? { backgroundColor: themeColors.primary, borderColor: themeColors.primary }
                : { backgroundColor: themeColors.surfaceElevated, borderColor: themeColors.border },
            ]}
            onPress={() => onSelect(n)}
            haptics
            accessibilityRole="button"
            accessibilityLabel={`${accessibility_prefix} ${n}`}
            accessibilityState={{ selected: is_selected }}>
            <View style={styles.customCountTileInner}>
              <Text
                style={[
                  styles.customCountTileValue,
                  { color: is_selected ? themeColors.primaryText : themeColors.text },
                  ANDROID_TEXT,
                ]}>
                {formatNumber(n)}
              </Text>
            </View>
          </Clickable2>
        );
      })}
    </View>
  );
}

function computeCustomAmount(args: {
  custom_days: number;
  meals: MealMacros[];
  snack_counter: number;
  one_snack_price: number;
  minimum_cost: number;
  price_protein: Record<string, number>;
  price_carbs: Record<string, number>;
}): number {
  const { custom_days, meals, snack_counter, one_snack_price, minimum_cost, price_protein, price_carbs } = args;
  if (custom_days <= 0) {
    return 0;
  }
  let sum = 0;
  for (let i = 0; i < meals.length; i++) {
    const p = meals[i];
    sum += custom_days * (price_protein[p?.protein ?? ''] ?? 0);
    sum += custom_days * (price_carbs[p?.carbs ?? ''] ?? 0);
  }
  sum += one_snack_price * snack_counter * custom_days;
  return Math.max(sum, minimum_cost);
}

export function CustomPlanBuilder({
  custom_plans,
  selected_category_id,
  onSelectCategory,
  particular_index,
  onSelectParticular,
  meal_counter,
  setMealCounter,
  snack_counter,
  setSnackCounter,
  protein_carbs_array,
  setProteinCarbsArray,
  onProceedToCheckout,
  is_logged_in,
  styles,
  themeColors,
  onScroll,
  refreshing,
  onRefresh,
  content_top_inset,
  content_bottom_inset,
}: {
  custom_plans: any[];
  selected_category_id: string | number | null;
  onSelectCategory: (id: string | number | null) => void;
  particular_index: number;
  onSelectParticular: (index: number) => void;
  meal_counter: number;
  setMealCounter: (n: number) => void;
  snack_counter: number;
  setSnackCounter: (n: number) => void;
  protein_carbs_array: Array<MealMacros>;
  setProteinCarbsArray: React.Dispatch<React.SetStateAction<Array<MealMacros>>>;
  onProceedToCheckout: (payload: CustomCheckoutPayload) => void;
  is_logged_in: boolean;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
  onSliderGestureActiveChange?: (active: boolean) => void;
  onScroll?: (event: any) => void;
  refreshing?: boolean;
  onRefresh?: () => void;
  content_top_inset?: number;
  content_bottom_inset?: number;
}) {
  const { t } = useTranslation();
  const { textAlign, writingDirection, flipRowStyle, chevronNextAlongAxis, trailingRowDisclosureChevron } = useRtl();
  const { height: window_height } = useWindowDimensions();
  const meal_column_scroll_max = Math.round(window_height * 0.48);
  const [editor, set_editor] = useState<EditorKind>(null);
  const [info_open, set_info_open] = useState(false);
  const [meal_session_picks, set_meal_session_picks] = useState<{ protein: boolean; carbs: boolean }>({
    protein: false,
    carbs: false,
  });

  const open_editor = useCallback((next: EditorKind) => {
    if (typeof next === 'string' && next.startsWith('meal-')) {
      set_meal_session_picks({ protein: false, carbs: false });
    }
    set_editor(next);
  }, []);

  const close_editor = useCallback(() => {
    set_editor(null);
    set_meal_session_picks({ protein: false, carbs: false });
  }, []);

  const selected_category = useMemo(
    () => custom_plans.find((c: any) => String(c.id ?? c.title) === String(selected_category_id)) ?? custom_plans[0] ?? null,
    [custom_plans, selected_category_id],
  );
  const particulars = useMemo(
    () => sortByDisplayOrder((selected_category?.particulars ?? []) as Array<{ display_order?: number; displayOrder?: number }>).concat() as any[],
    [selected_category],
  );
  const particular: any = particulars[particular_index] ?? particulars[0] ?? null;

  const min_meals = particular ? Number(particular.minimum_meals ?? 0) : 0;
  const max_meals = particular ? Number(particular.maximum_meals ?? 10) : 10;
  const min_snacks = particular ? Number(particular.minimum_snacks ?? 0) : 0;
  const max_snacks = particular ? Number(particular.maximum_snacks ?? 5) : 5;
  const custom_days = particular ? Number(particular.days ?? particular.type_key ?? 0) : 0;
  const price_protein =
    particular?.price_protein && typeof particular.price_protein === 'object' ? (particular.price_protein as Record<string, number>) : {};
  const price_carbs =
    particular?.price_carbs && typeof particular.price_carbs === 'object' ? (particular.price_carbs as Record<string, number>) : {};
  const protein_keys = Object.keys(price_protein);
  const carbs_keys = Object.keys(price_carbs);
  const one_snack_price = particular ? Number(particular.price_per_snack ?? 0) : 0;
  const minimum_cost = particular ? Number(particular.minimum_cost ?? 0) : 0;
  const default_protein = protein_keys[0] ?? '';
  const default_carbs = carbs_keys[0] ?? '';

  const meals_for_pricing = useMemo(
    () => protein_carbs_array.slice(0, meal_counter),
    [protein_carbs_array, meal_counter],
  );

  const custom_amount = useMemo(
    () =>
      computeCustomAmount({
        custom_days,
        meals: meals_for_pricing,
        snack_counter,
        one_snack_price,
        minimum_cost,
        price_protein,
        price_carbs,
      }),
    [custom_days, meals_for_pricing, snack_counter, one_snack_price, minimum_cost, price_protein, price_carbs],
  );

  const meals_configured = meals_for_pricing.filter(m => Boolean(m?.protein && m?.carbs)).length;
  const macros_incomplete = meal_counter > 0 && meals_configured < meal_counter;
  const proceed_disabled = !particular || !selected_category || meal_counter === 0;

  const fill_meal_defaults = useCallback(
    (meals: MealMacros[]): MealMacros[] =>
      meals.map(m => ({
        protein: m?.protein || default_protein,
        carbs: m?.carbs || default_carbs,
      })),
    [default_protein, default_carbs],
  );

  const sync_meals = useCallback(
    (next: number) => {
      const clamped = Math.max(min_meals, Math.min(max_meals, next));
      setMealCounter(clamped);
      setProteinCarbsArray(prev => {
        if (clamped <= prev.length) {
          return prev.slice(0, clamped);
        }
        return [
          ...prev,
          ...Array(clamped - prev.length)
            .fill(null)
            .map(() => ({ protein: '', carbs: '' })),
        ];
      });
    },
    [min_meals, max_meals, setMealCounter, setProteinCarbsArray],
  );

  const set_meal_field = useCallback(
    (meal_idx: number, field: 'protein' | 'carbs', key: string) => {
      setProteinCarbsArray(prev => {
        const next = [...prev];
        while (next.length <= meal_idx) {
          next.push({ protein: '', carbs: '' });
        }
        next[meal_idx] = { ...next[meal_idx], [field]: key };
        return next;
      });
    },
    [setProteinCarbsArray],
  );

  const select_meal_macro = useCallback(
    (meal_idx: number, field: 'protein' | 'carbs', key: string) => {
      set_meal_field(meal_idx, field, key);
      set_meal_session_picks(prev => {
        const next = { ...prev, [field]: true };
        const protein_ok = protein_keys.length === 0 || next.protein;
        const carbs_ok = carbs_keys.length === 0 || next.carbs;
        if (protein_ok && carbs_ok) {
          // Defer close so state updates settle before unmounting the sheet.
          setTimeout(() => close_editor(), 0);
        }
        return next;
      });
    },
    [set_meal_field, close_editor, protein_keys.length, carbs_keys.length],
  );

  const proceed_with_meals = useCallback(
    (meals: MealMacros[]) => {
      if (!selected_category || !particular) {
        return;
      }
      const cost = computeCustomAmount({
        custom_days,
        meals,
        snack_counter,
        one_snack_price,
        minimum_cost,
        price_protein,
        price_carbs,
      });
      onProceedToCheckout({
        isCustomAllowed: true,
        subscription_category_id: selected_category.id,
        subscription_id: selected_category.id,
        days: custom_days,
        number_of_meals: meal_counter,
        number_of_snacks: snack_counter,
        customized_meals: meals,
        cost,
        title: getApiText(selected_category as unknown as Record<string, unknown>, 'title', 'arabic_title') || selected_category.title,
        currency: getDefaultCurrencyCode(),
        no_meals_on_days: [],
        max_day_off: 0,
        celebrity_id: 0,
      });
    },
    [
      selected_category,
      particular,
      custom_days,
      meal_counter,
      snack_counter,
      one_snack_price,
      minimum_cost,
      price_protein,
      price_carbs,
      onProceedToCheckout,
    ],
  );

  const handleProceed = useCallback(() => {
    if (proceed_disabled) {
      return;
    }
    const current = Array.from({ length: meal_counter }, (_, i) => protein_carbs_array[i] ?? { protein: '', carbs: '' });
    const incomplete = current.some(m => !m.protein || !m.carbs);
    if (incomplete) {
      Alert.alert(
        t('subscriptions.incompleteMealMacrosTitle') || 'Meal macros incomplete',
        t('subscriptions.incompleteMealMacrosMessage') ||
          'Some meals are missing protein or carbs. You can continue with default values, or go back and set them.',
        [
          { text: t('common.cancel') || 'Cancel', style: 'cancel' },
          {
            text: t('subscriptions.continueWithDefaults') || 'Continue with defaults',
            onPress: () => {
              const filled = fill_meal_defaults(current);
              setProteinCarbsArray(filled);
              proceed_with_meals(filled);
            },
          },
        ],
      );
      return;
    }
    proceed_with_meals(current);
  }, [
    proceed_disabled,
    meal_counter,
    protein_carbs_array,
    t,
    fill_meal_defaults,
    setProteinCarbsArray,
    proceed_with_meals,
  ]);

  const custom_slider_items = useMemo(() => custom_plans.map((c: any) => ({ id: String(c.id ?? c.title ?? ''), ...c })), [custom_plans]);

  const meal_editor_index = editor?.startsWith('meal-') ? Number(editor.replace('meal-', '')) : -1;
  const meal_macros = meal_editor_index >= 0 ? protein_carbs_array[meal_editor_index] : null;

  const editor_title = useMemo(() => {
    if (editor === 'duration') {
      return t('subscriptions.duration') || 'Duration';
    }
    if (editor === 'meals') {
      return t('subscriptions.meals') || 'Meals';
    }
    if (editor === 'snacks') {
      return t('subscriptions.snacks') || 'Snacks';
    }
    if (meal_editor_index >= 0) {
      return `${t('subscriptions.meal') || 'Meal'} ${formatNumber(meal_editor_index + 1)}`;
    }
    return '';
  }, [editor, meal_editor_index, t]);

  if (!selected_category) {
    return null;
  }

  const proceed_label = is_logged_in
    ? t('subscriptions.proceedToCheckout') || 'Proceed to checkout'
    : t('subscriptions.signUpToCheckout') || 'Sign up to checkout';

  const set_macros_hint = t('subscriptions.setMealMacros') || 'Choose macros';
  const scaled_protein_label = t('subscriptions.scaledProtein') || 'Scaled protein';
  const scaled_carbs_label = t('subscriptions.scaledCarbs') || 'Scaled carbs';
  const meals_progress =
    meal_counter > 0
      ? t('subscriptions.mealMacrosProgress', {
          configured: formatNumber(meals_configured),
          total: formatNumber(meal_counter),
        }) || `${formatNumber(meals_configured)} of ${formatNumber(meal_counter)}`
      : '';

  const setup_duration =
    particulars.length > 0
      ? {
          key: 'duration',
          label: t('subscriptions.duration') || 'Duration',
          value: `${formatNumber(custom_days)} ${t('subscriptions.daysLabel')}`,
          onPress: () => open_editor('duration'),
        }
      : null;
  const meals_label = t('subscriptions.meals') || 'Meals';
  const snacks_label = t('subscriptions.snacks') || 'Snacks';

  const top_inset = content_top_inset ?? appTheme.spacing.md;
  const bottom_inset = content_bottom_inset ?? appTheme.spacing.md;
  const footer_fade_colors = [hexToRgba(themeColors.surface, 0), hexToRgba(themeColors.surface, 0.98)] as const;

  return (
    <View style={styles.customBuilderRoot}>
      <Animated.ScrollView
        style={styles.customBuilderScroll}
        contentContainerStyle={[
          styles.customBuilderContent,
          {
            paddingTop: top_inset,
            paddingBottom: appTheme.spacing.xl,
          },
        ]}
        contentInsetAdjustmentBehavior="never"
        automaticallyAdjustContentInsets={false}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={16}
        refreshControl={
          onRefresh ? (
            <RefreshControl refreshing={Boolean(refreshing)} onRefresh={onRefresh} tintColor={themeColors.primary} />
          ) : undefined
        }>
        {custom_plans.length > 0 ? (
          <View style={styles.customCategoryRow}>
            <CategorySlider
              items={custom_slider_items}
              getImageUri={(c: any) => {
                const primary = resolveAttachmentUri(c.attachment);
                return primary ?? firstPlanImageFromCustomCategory(c);
              }}
              getTitle={(c: any) => getApiText(c as unknown as Record<string, unknown>, 'title', 'arabic_title') || c.title || ''}
              selectedId={selected_category_id}
              onSelect={onSelectCategory}
              styles={styles}
              themeColors={themeColors}
              compact
            />
          </View>
        ) : null}

        <View style={styles.customComposeCard}>
          <View style={styles.customSummaryHeader}>
            <Text style={[styles.customSummaryEyebrow, { textAlign, writingDirection }, ANDROID_TEXT]}>
              {t('subscriptions.buildYourPlan') || 'Build your plan'}
            </Text>
          </View>

          <View style={styles.customSummaryList}>
            {setup_duration ? (
              <SummaryRow
                key={setup_duration.key}
                label={setup_duration.label}
                value={setup_duration.value}
                onPress={setup_duration.onPress}
                styles={styles}
                themeColors={themeColors}
                chevron={trailingRowDisclosureChevron}
                is_last={false}
              />
            ) : null}
            <MealsSnacksSetupRow
              meals_label={meals_label}
              snacks_label={snacks_label}
              meal_count={meal_counter}
              snack_count={snack_counter}
              onPressMeals={() => open_editor('meals')}
              onPressSnacks={() => open_editor('snacks')}
              styles={styles}
              themeColors={themeColors}
              is_last
            />
          </View>

          {meal_counter > 0 ? (
            <View style={styles.customSection}>
              <View style={[styles.customMealsHeader, flipRowStyle]}>
                <View style={[styles.customMealsHeaderTitleRow, flipRowStyle]}>
                  <Text style={[styles.customMealsHeaderLabel, { textAlign, writingDirection }, ANDROID_TEXT]}>
                    {t('subscriptions.macros') || 'Macros per meal'}
                  </Text>
                  <Clickable2
                    style={styles.customMealsInfoBtn}
                    onPress={() => set_info_open(true)}
                    haptics
                    accessibilityRole="button"
                    accessibilityLabel={t('subscriptions.customPlanInfoA11y') || 'How custom plans work'}>
                    <SvgIcon name="info" size={18} color={themeColors.primary} />
                  </Clickable2>
                </View>
                <Text style={[styles.customMealsHeaderCount, ANDROID_TEXT]}>{meals_progress}</Text>
              </View>
              <Text style={[styles.customMacroLegend, { textAlign, writingDirection }, ANDROID_TEXT]} numberOfLines={2}>
                {`P · ${scaled_protein_label}   ·   C · ${scaled_carbs_label}`}
              </Text>
              <View style={styles.customMealSlots}>
                {Array.from({ length: meal_counter }).map((_, i) => {
                  const macros = protein_carbs_array[i];
                  return (
                    <MealSlot
                      key={`meal-${i}`}
                      index={i}
                      protein={macros?.protein || undefined}
                      carbs={macros?.carbs || undefined}
                      onPress={() => open_editor(`meal-${i}`)}
                      styles={styles}
                      themeColors={themeColors}
                      chevron={trailingRowDisclosureChevron}
                      meal_label={`${t('subscriptions.meal') || 'Meal'} ${formatNumber(i + 1)}`}
                      set_macros_hint={set_macros_hint}
                      scaled_protein_label={scaled_protein_label}
                      scaled_carbs_label={scaled_carbs_label}
                    />
                  );
                })}
              </View>
            </View>
          ) : null}
        </View>
      </Animated.ScrollView>

      <View style={[styles.customStickyFooter, { paddingBottom: Math.max(bottom_inset, appTheme.spacing.md) }]}>
        <LinearGradient colors={[...footer_fade_colors]} style={styles.customStickyFooterFade} pointerEvents="none" />
        <Clickable2
          style={[styles.customProceedBtn, proceed_disabled && styles.customProceedBtnDisabled]}
          onPress={handleProceed}
          disabled={proceed_disabled}
          haptics
          accessibilityRole="button"
          accessibilityLabel={proceed_label}
          accessibilityState={{ disabled: proceed_disabled }}
          >
          <Text style={[styles.customProceedPrice, ANDROID_TEXT]} numberOfLines={1}>
            {formatCurrency(custom_amount)}
          </Text>
          <View style={[styles.customProceedInner, flipRowStyle]}>
            <Text style={[styles.customProceedText, ANDROID_TEXT]} numberOfLines={1}>
              {proceed_label}
            </Text>
            <SvgIcon name={chevronNextAlongAxis} size={14} color={themeColors.onPrimary} />
          </View>
        </Clickable2>
      </View>

      <BottomSheetPopup
        visible={editor != null}
        onClose={close_editor}
        title={editor_title}
        animationType="slide"
        showDragHandle
        sheetHeightMode="fitContent"
        sheetStyle={{ maxHeight: '72%', backgroundColor: themeColors.backgroundElevated }}>
        <View style={styles.customEditorBody}>
          {editor === 'duration'
            ? particulars.map((p: any, idx: number) => {
                const days = Number(p.days ?? p.type_key ?? 0);
                return (
                  <OptionRow
                    key={idx}
                    label={`${formatNumber(days)} ${t('subscriptions.daysLabel')}`}
                    selected={idx === particular_index}
                    onPress={() => {
                      onSelectParticular(idx);
                      close_editor();
                    }}
                    styles={styles}
                    themeColors={themeColors}
                  />
                );
              })
            : null}

          {editor === 'meals' ? (
            <CountGrid
              values={Array.from({ length: Math.max(0, max_meals - min_meals + 1) }, (_, i) => min_meals + i)}
              selected={meal_counter}
              onSelect={n => {
                sync_meals(n);
                close_editor();
              }}
              styles={styles}
              themeColors={themeColors}
              accessibility_prefix={t('subscriptions.meals') || 'Meals'}
            />
          ) : null}

          {editor === 'snacks' ? (
            <CountGrid
              values={Array.from({ length: Math.max(0, max_snacks - min_snacks + 1) }, (_, i) => min_snacks + i)}
              selected={snack_counter}
              onSelect={n => {
                setSnackCounter(n);
                close_editor();
              }}
              styles={styles}
              themeColors={themeColors}
              accessibility_prefix={t('subscriptions.snacks') || 'Snacks'}
            />
          ) : null}

          {meal_editor_index >= 0 ? (
            <View style={[styles.customMacroColumns, flipRowStyle]}>
              {protein_keys.length > 0 ? (
                <View style={styles.customMacroColumn}>
                  <Text style={[styles.customMacroGroupLabel, { textAlign: 'center', writingDirection }, ANDROID_TEXT]}>
                    {t('dashboard.protein')}
                  </Text>
                  <ScrollView
                    style={[styles.customMacroColumnScroll, { maxHeight: meal_column_scroll_max }]}
                    contentContainerStyle={styles.customMacroColumnScrollContent}
                    showsVerticalScrollIndicator
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled">
                    {protein_keys.map(k => (
                      <OptionRow
                        key={`p-${k}`}
                        label={`${k}g`}
                        selected={meal_macros?.protein === k}
                        onPress={() => select_meal_macro(meal_editor_index, 'protein', k)}
                        styles={styles}
                        themeColors={themeColors}
                        compact
                      />
                    ))}
                  </ScrollView>
                </View>
              ) : null}
              {carbs_keys.length > 0 ? (
                <View style={styles.customMacroColumn}>
                  <Text style={[styles.customMacroGroupLabel, { textAlign: 'center', writingDirection }, ANDROID_TEXT]}>
                    {t('dashboard.carbs')}
                  </Text>
                  <ScrollView
                    style={[styles.customMacroColumnScroll, { maxHeight: meal_column_scroll_max }]}
                    contentContainerStyle={styles.customMacroColumnScrollContent}
                    showsVerticalScrollIndicator
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled">
                    {carbs_keys.map(k => (
                      <OptionRow
                        key={`c-${k}`}
                        label={`${k}g`}
                        selected={meal_macros?.carbs === k}
                        onPress={() => select_meal_macro(meal_editor_index, 'carbs', k)}
                        styles={styles}
                        themeColors={themeColors}
                        compact
                      />
                    ))}
                  </ScrollView>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      </BottomSheetPopup>

      <BottomSheetPopup
        visible={info_open}
        onClose={() => set_info_open(false)}
        title={t('subscriptions.customPlanInfoTitle') || 'How custom plans work'}
        animationType="slide"
        showDragHandle
        sheetHeightMode="fitContent"
        sheetStyle={{ maxHeight: '78%', backgroundColor: themeColors.backgroundElevated }}>
        <ScrollView
          style={styles.customInfoScroll}
          contentContainerStyle={styles.customInfoBody}
          showsVerticalScrollIndicator={false}
          bounces={false}>
          <Text style={[styles.customInfoParagraph, { textAlign, writingDirection, color: themeColors.text }, ANDROID_TEXT]}>
            {t('subscriptions.customPlanInfoIntro') ||
              'Build a plan that matches how you eat: pick the length, how many meals and snacks you want, then set protein and carbs for each meal.'}
          </Text>
          <Text style={[styles.customInfoHeading, { textAlign, writingDirection, color: themeColors.text }, ANDROID_TEXT]}>
            {t('subscriptions.customPlanInfoStepsTitle') || 'What you choose'}
          </Text>
          <Text style={[styles.customInfoParagraph, { textAlign, writingDirection, color: themeColors.textSecondary }, ANDROID_TEXT]}>
            {t('subscriptions.customPlanInfoStepsBody') ||
              'Duration sets how many plan days you buy. Meals and snacks set how many of each you get per day. Each meal has its own scaled protein and scaled carbs targets.'}
          </Text>
          <Text style={[styles.customInfoHeading, { textAlign, writingDirection, color: themeColors.text }, ANDROID_TEXT]}>
            {`${scaled_protein_label} & ${scaled_carbs_label}`}
          </Text>
          <Text style={[styles.customInfoParagraph, { textAlign, writingDirection, color: themeColors.textSecondary }, ANDROID_TEXT]}>
            {t('subscriptions.customPlanInfoMacrosBody') ||
              'Scaled protein (P) and scaled carbs (C) are the portion levels for that meal slot. They help size your meals to your goals. Tap a meal to pick both values.'}
          </Text>
          <Text style={[styles.customInfoHeading, { textAlign, writingDirection, color: themeColors.text }, ANDROID_TEXT]}>
            {t('subscriptions.customPlanInfoPricingTitle') || 'How pricing works'}
          </Text>
          <Text style={[styles.customInfoParagraph, { textAlign, writingDirection, color: themeColors.textSecondary }, ANDROID_TEXT]}>
            {t('subscriptions.customPlanInfoPricingBody') ||
              'Your total is based on plan duration, the protein and carb levels you set for each meal, and the number of snacks. A minimum plan price may apply. You can change any option before checkout.'}
          </Text>
        </ScrollView>
      </BottomSheetPopup>
    </View>
  );
}
